import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { Position } from '@xyflow/react';
import { memo, useState } from 'react';

import { DashedTag } from '@/components/chariot-ui';
import { ConditionalRender } from '@/components/ConditionalRender';
import { Popover } from '@/components/Popover';
import { Button } from '@/components/ui/button';
import { NodeHandles } from '@/sections/attacks/attackPaths/components/nodes/NodeHandles';
import { TechniqueModal } from '@/sections/attacks/attackPaths/components/wizards/TechniqueModal';
import { techniqueColors } from '@/sections/attacks/attackPaths/constants';
import { useAttackState } from '@/sections/attacks/attackPaths/context';
import mitreData from '@/sections/attacks/attackPaths/data.json';
import type { CustomTechniqueNode } from '@/sections/attacks/attackPaths/types';
import { cn } from '@/utils/classname';

const allTechniques = mitreData.flatMap(tactic => tactic.techniques);

interface TechniqueNodeComponentProps {
  data: CustomTechniqueNode['data'];
  id: string;
  sourcePosition?: Position;
  targetPosition?: Position;
}

/**
 * Technique node component for attack path visualization.
 *
 * Features:
 * - Display MITRE ATT&CK technique with status color coding
 * - View mode: Hover popover with technique details, click opens MITRE ATT&CK page
 * - Edit mode: Opens modal for editing via TechniqueModal
 * - Learn more link to MITRE ATT&CK website
 * - Handles for connecting edges
 */
function TechniqueNodeComponent({
  data,
  id,
  sourcePosition,
  targetPosition,
}: TechniqueNodeComponentProps) {
  const [hoverPopoverOpen, setHoverPopoverOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const { removeNode, isEditMode } = useAttackState();

  const currentTechnique = data.id
    ? allTechniques.find(technique => technique.id === data.id)
    : undefined;

  // Create node object for edit modal
  const nodeForEdit: CustomTechniqueNode = {
    id,
    type: 'technique',
    position: { x: 0, y: 0 },
    data,
  };

  /**
   * Handle click on node in edit mode - open edit modal
   */
  const handleEditClick = () => {
    if (isEditMode) {
      setEditModalOpen(true);
    }
  };

  /**
   * Open the MITRE ATT&CK page for the current technique in a new tab.
   */
  const openMitreAttackPage = () => {
    if (currentTechnique?.id) {
      window.open(
        `https://attack.mitre.org/techniques/${currentTechnique.id}/`,
        '_blank'
      );
    }
  };

  return (
    <>
      <ConditionalRender
        condition={isEditMode}
        conditionalWrapper={children => (
          <div
            role="button"
            tabIndex={0}
            onClick={handleEditClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleEditClick();
              }
            }}
            className="cursor-pointer"
            aria-label="Edit technique node"
          >
            {children}
          </div>
        )}
        elseWrapper={children => (
          // View mode: Show hover popover with technique details
          <ConditionalRender
            condition={Boolean(currentTechnique)}
            conditionalWrapper={innerChildren => (
              <Popover
                open={hoverPopoverOpen}
                setOpen={setHoverPopoverOpen}
                placement="right-start"
                interactions="hover"
                container={
                  <button
                    type="button"
                    onClick={openMitreAttackPage}
                    className="cursor-pointer"
                    aria-label={`View ${currentTechnique?.name || 'technique'} on MITRE ATT&CK`}
                  >
                    {innerChildren}
                  </button>
                }
                className="flex max-w-sm min-w-[300px] flex-col gap-4"
              >
                <div
                  className="flex flex-col gap-3 p-2"
                  onClick={event => event.stopPropagation()}
                >
                  <h1 className="text-chariot-text-primary text-lg font-semibold">
                    {currentTechnique?.name || ''}
                  </h1>
                  <DashedTag size="sm" label={currentTechnique?.id || ''} />
                  <p className="text-chariot-text-secondary line-clamp-6 text-sm">
                    {currentTechnique?.description || ''}
                  </p>
                  <Button
                    onClick={openMitreAttackPage}
                    variant="ghost"
                    className="mt-2 w-fit"
                  >
                    Learn more
                    <ArrowTopRightOnSquareIcon className="size-4" />
                  </Button>
                </div>
              </Popover>
            )}
          >
            {children}
          </ConditionalRender>
        )}
      >
        {/* h-[72px]+justify-center centers the card so its handle sits at dims.height/2=36 regardless of card content height (see getNodeDimensions) */}
        <div className="relative flex h-[72px] w-[200px] flex-col items-center justify-center">
          {/* Technique card — compact pill for short IDs, wider card for narrative names */}
          <div
            className={cn(
              'relative flex max-w-full items-center gap-1.5 rounded-lg border-2 px-3 py-1.5',
              techniqueColors[data.status || '']?.bg,
              techniqueColors[data.status || '']?.border
            )}
          >
            <div className="flex flex-col items-center gap-0.5">
              <div
                className={cn(
                  'text-[10px] font-bold uppercase opacity-80',
                  techniqueColors[data.status || '']?.text ||
                    'text-chariot-text-secondary'
                )}
              >
                {data.id || 'T----'}
              </div>
              {(data.name || currentTechnique?.name) && (
                <div
                  className={cn(
                    'text-xs leading-tight font-semibold wrap-break-word',
                    techniqueColors[data.status || '']?.text ||
                      'text-chariot-text-primary'
                  )}
                >
                  {data.name || currentTechnique?.name || ''}
                </div>
              )}
            </div>
            {isEditMode && (
              <button
                className="bg-chariot-status-critical hover:bg-chariot-status-critical/80 absolute -top-1.5 -right-1.5 rounded-full p-[2px]"
                onClick={event => {
                  event.stopPropagation();
                  removeNode(id);
                }}
                aria-label="Remove node"
              >
                <XMarkIcon className="text-chariot-text-primary size-3 stroke-[4px]" />
              </button>
            )}
            {/* Anchor handles to the card edges (matches AssetNode/etc.) so
                xyflow's Left/Right handles attach to the card, not the node. */}
            <NodeHandles
              isEditMode={isEditMode}
              nodeId={id}
              sourcePosition={sourcePosition}
              targetPosition={targetPosition}
            />
          </div>
        </div>
      </ConditionalRender>

      {/* Edit Modal */}
      <TechniqueModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        mode="edit"
        initialNode={nodeForEdit}
      />
    </>
  );
}

export const TechniqueNode = memo(TechniqueNodeComponent);
